import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "flush", "singleBreadcrumbIsBackLink", "truncateToLevels", "wrapLevels", "role", "defaultOpen", "backButtonOnClick"];
import { Children, cloneElement, isValidElement, Fragment } from 'react';
import styled from 'foundations-theming/utils';
import { useHeaderContext } from '../Header/Header';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useToolBarContext } from '../ToolBar/ToolBarContext';
import { useBackButtonContext } from '../BackButton/BackButton';
import { MERCURY_LAYER } from '../../constants';
import { Dropdown } from '../Dropdown';
import { List, ListItem } from '../List';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { warnIfFragment } from '../../utils/warnIfFragment';
import { StyledLink } from '../Link/Link';
import { SIZE } from '../../../constants/nonTokenisedValues';
import { SuppressLinkUnderlineContextProvider } from '../Link/SuppressLinkUnderlineContext';
import { ControlProvider } from '../Link/ControlContext';
import { EllipsesIcon } from 'foundations-assets/icons/components/EllipsesIcon';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledBreadcrumbInnerWrapper = styled.span.withConfig({
  displayName: "Breadcrumbs__StyledBreadcrumbInnerWrapper",
  componentId: "sc-1iqm8vk-0"
})(({
  $isBackLink
}) => {
  return Object.assign({}, $isBackLink && {
    display: 'inline-flex',
    alignItems: 'center'
  }, {
    'button &': {
      display: 'inline-flex',
      alignItems: 'center'
    }
  });
});
const StyledBreadcrumbItem = styled.span.withConfig({
  displayName: "Breadcrumbs__StyledBreadcrumbItem",
  componentId: "sc-1iqm8vk-1"
})(() => {
  return {
    position: 'relative',
    verticalAlign: 'middle'
  };
});
const StyledIconSpan = styled.span.withConfig({
  displayName: "Breadcrumbs__StyledIconSpan",
  componentId: "sc-1iqm8vk-2"
})(({
  theme
}) => {
  return {
    display: 'flex',
    alignItems: 'center !important',
    '& > svg': {
      marginInlineStart: `${theme.space['100']} !important`,
      marginInlineEnd: `${theme.space['0']} !important`
    }
  };
});
export const StyledIconWrapper = styled.div.withConfig({
  displayName: "Breadcrumbs__StyledIconWrapper",
  componentId: "sc-1iqm8vk-3"
})(({
  theme,
  $isBackLink,
  $allowWrap,
  $isWithinToolBar,
  $isInBackButton
}) => {
  function getIconSize() {
    // This is deliberately different from UIComponents and knowingly breaks parity!
    // This intentionally chooses a middle ground size icon between the icon that felt "too big"
    // when a BackButton was used in a Toolbar, vs the icon that felt "too small" when used
    // standalone. This sizing here is a compromise between the two and keeps all uses consistent
    // using the same size icon.
    // This change was confirmed with UX. All BackButton icons should now be the same size.
    if ($isInBackButton) {
      return theme.typography['heading-100'].fontSize;
    }
    return SIZE.BREADCRUMBS_ICON;
  }
  const iconSize = getIconSize();
  const getMarginInlineEnd = () => {
    if ($isWithinToolBar && $isInBackButton) {
      return theme.space['50'];
    }
    if ($isInBackButton) {
      return theme.space['200'];
    }
    return theme.space['100'];
  };
  return {
    marginInlineEnd: getMarginInlineEnd(),
    alignItems: 'center',
    display: 'inline-flex',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
    fontSize: iconSize,
    inlineSize: iconSize,
    marginInlineStart: $isBackLink ? theme.space['0'] : theme.space['100'],
    verticalAlign: $isWithinToolBar || $allowWrap ? 'middle' : undefined
  };
});
const renderBreadcrumb = (children, hasArrow, isBackLink, allowWrap, backButtonOnClick, isWithinToolBar, isInBackButton) => {
  const ArrowIcon = isBackLink ? LeftIcon : RightIcon;
  return /*#__PURE__*/_jsxs(StyledBreadcrumbInnerWrapper, Object.assign({
    $isBackLink: isBackLink
  }, backButtonOnClick && {
    role: 'button',
    // accessibility
    tabIndex: 0,
    // makes the span focusable
    onClick: backButtonOnClick,
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault(); // Prevent scrolling when Space is pressed
        backButtonOnClick(e);
      }
    }
  }, {
    children: [hasArrow && /*#__PURE__*/_jsx(StyledIconWrapper, {
      $isBackLink: isBackLink,
      $allowWrap: allowWrap,
      $isWithinToolBar: isWithinToolBar || false,
      $isInBackButton: isInBackButton || false,
      children: /*#__PURE__*/_jsx(ArrowIcon, {
        color: "icon-core-subtle"
      })
    }), children]
  }));
};
const renderChild = (child, index, totalCrumbs, isOnlyChild = false, singleBreadcrumbIsBackLink = false, isSROnly = false, allowWrap = false, backButtonOnClick, isWithinToolBar, isInBackButton) => {
  const ariaCurrent = !isOnlyChild && index === totalCrumbs - 1 ? 'location' : undefined;
  const isBackLink = isOnlyChild && singleBreadcrumbIsBackLink;
  const hasArrow = isBackLink || index !== 0;
  warnIfFragment(child, 'Breadcrumbs');
  if ( /*#__PURE__*/isValidElement(child)) {
    const clonedElement = /*#__PURE__*/cloneElement(child, {
      //@ts-expect-error - TS doesn't like aria-current on non-HTML elements
      'aria-current': ariaCurrent,
      children: renderBreadcrumb(child.props.children, hasArrow, isBackLink, allowWrap, backButtonOnClick, isWithinToolBar, isInBackButton),
      key: index,
      style: {
        position: 'relative',
        verticalAlign: 'middle'
      }
    });
    return isSROnly ? /*#__PURE__*/_jsx(ScreenReaderOnly, {
      children: clonedElement
    }, index) : clonedElement;
  }
  return /*#__PURE__*/_jsx(StyledBreadcrumbItem, {
    children: renderBreadcrumb(child, hasArrow, isBackLink, allowWrap, undefined, isWithinToolBar, isInBackButton)
  });
};
const renderTruncateDropdown = (children, defaultOpen, isWithinToolBar, isInBackButton) => {
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(StyledBreadcrumbInnerWrapper, {
      children: /*#__PURE__*/_jsx(StyledIconWrapper, {
        $isBackLink: false,
        $isWithinToolBar: isWithinToolBar || false,
        $isInBackButton: isInBackButton || false,
        children: /*#__PURE__*/_jsx(RightIcon, {
          color: "icon-core-subtle"
        })
      })
    }), /*#__PURE__*/_jsx(Dropdown, {
      buttonUse: "transparent",
      buttonText: /*#__PURE__*/_jsx(StyledBreadcrumbInnerWrapper, {
        children: /*#__PURE__*/_jsx(StyledIconSpan, {
          children: /*#__PURE__*/_jsx(EllipsesIcon, {
            size: "300",
            color: "icon-core-subtle"
          })
        })
      }),
      menuWidth: "auto",
      caretRenderer: null,
      defaultOpen: defaultOpen,
      buttonStyles: {
        padding: '0'
      },
      children: /*#__PURE__*/_jsx(SuppressLinkUnderlineContextProvider, {
        suppressUnderline: true,
        children: /*#__PURE__*/_jsx(List, {
          children: Children.map(children, child => {
            if ( /*#__PURE__*/isValidElement(child)) {
              return /*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/cloneElement(child, {
                  key: child.key || undefined
                })
              }, child.key || undefined);
            }
            return child;
          })
        })
      })
    })]
  });
};
const renderTruncatedBreadcrumbs = (validChildren, isOnlyChild, singleBreadcrumbIsBackLink, levelsToShow, defaultOpen, allowWrap = false, isWithinToolBar, isInBackButton) => {
  const totalCrumbs = validChildren.length;
  const innerCrumbs = validChildren.slice(1, -levelsToShow);
  const innerCrumbLength = innerCrumbs.length;
  const updatedLevelsToShow = innerCrumbLength ? levelsToShow : totalCrumbs - 1;
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [renderChild(validChildren[0], 0, totalCrumbs, isOnlyChild, singleBreadcrumbIsBackLink, false, allowWrap, undefined, isWithinToolBar, isInBackButton), innerCrumbs.map((child, index) => {
      return renderChild(child, index + 1, totalCrumbs, isOnlyChild, singleBreadcrumbIsBackLink, true, allowWrap, undefined, isWithinToolBar, isInBackButton);
    }), innerCrumbLength ? renderTruncateDropdown(innerCrumbs, defaultOpen, isWithinToolBar, isInBackButton) : null, validChildren.map((child, index) => {
      if (index > totalCrumbs - updatedLevelsToShow - 1) {
        return renderChild(child, index, totalCrumbs, isOnlyChild, singleBreadcrumbIsBackLink, false, allowWrap, undefined, isWithinToolBar, isInBackButton);
      }
      return null;
    })]
  });
};
const renderChildren = (children, singleBreadcrumbIsBackLink, truncateToLevels, defaultOpen, allowWrap = false, backButtonOnClick, isWithinToolBar, isInBackButton) => {
  const isValidChild = child => {
    if (typeof child === 'string') {
      return child.trim().length > 0;
    }
    return child;
  };
  const childrenArray = Children.toArray(children);
  const validChildren = childrenArray.filter(isValidChild);
  const totalCrumbs = validChildren.length;
  const isOnlyChild = totalCrumbs === 1;
  let breadcrumbs;
  if (truncateToLevels != null && !isOnlyChild) {
    breadcrumbs = renderTruncatedBreadcrumbs(validChildren, isOnlyChild, singleBreadcrumbIsBackLink, truncateToLevels, defaultOpen, allowWrap, isWithinToolBar, isInBackButton);
  } else {
    breadcrumbs = Children.map(validChildren, (child, index) => {
      return renderChild(child, index, totalCrumbs, isOnlyChild, singleBreadcrumbIsBackLink, false, allowWrap, backButtonOnClick, isWithinToolBar, isInBackButton);
    });
  }
  if (isOnlyChild && !singleBreadcrumbIsBackLink) {
    breadcrumbs.push( /*#__PURE__*/_jsx(StyledBreadcrumbItem, {
      children: renderBreadcrumb(null, true, false, allowWrap, undefined, isWithinToolBar, isInBackButton)
    }, "x"));
  }
  return breadcrumbs;
};
export const StyledNav = styled.nav.withConfig({
  displayName: "Breadcrumbs__StyledNav",
  componentId: "sc-1iqm8vk-4"
})(({
  $cssStyles,
  $flush,
  $noWrap,
  $isInHeader,
  $isInToolBar,
  $isInBackButton,
  theme
}) => {
  return Object.assign({
    fontSize: !$isInBackButton ? theme.typography['detail-100'].fontSize : 'inherit',
    color: theme.color['text-core-subtle'],
    marginTop: $isInHeader || $isInToolBar || $isInBackButton ? theme.space['0'] : theme.space['400'],
    padding: `0 ${$flush ? theme.space['0'] : theme.space['1100']}`,
    position: 'relative',
    display: $isInBackButton ? 'inline-block' : 'block',
    zIndex: MERCURY_LAYER,
    [`${StyledLink}`]: {
      display: 'unset',
      position: 'relative',
      verticalAlign: 'middle'
    }
  }, $noWrap && {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }, $cssStyles);
});
export const Breadcrumbs = _ref => {
  let {
      children,
      flush = false,
      singleBreadcrumbIsBackLink = true,
      truncateToLevels = null,
      wrapLevels = false,
      role = 'navigation',
      defaultOpen = false,
      backButtonOnClick
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    header
  } = useHeaderContext();
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const isWithinToolBar = !!useToolBarContext().use;
  const isInBackButton = useBackButtonContext().backButton;
  if (header || isWithinToolBar) {
    flush = true;
  }
  const noWrap = !(wrapLevels || truncateToLevels && truncateToLevels > 2);
  return /*#__PURE__*/_jsx(ControlProvider, {
    isControl: true,
    children: /*#__PURE__*/_jsx(StyledNav, Object.assign({
      role: role,
      $flush: flush,
      $noWrap: noWrap,
      $cssStyles: cssStyles,
      $isInHeader: header,
      $isInToolBar: isWithinToolBar,
      $isInBackButton: isInBackButton
    }, domProps, {
      children: renderChildren(children, singleBreadcrumbIsBackLink, truncateToLevels, defaultOpen, wrapLevels, backButtonOnClick, isWithinToolBar, isInBackButton)
    }))
  });
};